/**
 * The moment recognition lands.
 *
 * Each recognised notehead is drawn as clean notation positioned exactly where
 * you drew it, then animated to the line or space it actually belongs on. The raw
 * ink underneath fades out over the same beat, so the mark you made appears to
 * *become* the note. Chord tones converge on a shared x, which is what makes a
 * hand-drawn stack visibly snap into a vertical chord.
 *
 * Identity is keyed on the source strokes, so re-running recognition after the
 * next stroke does not restart animations that have already finished.
 */

import { useEffect, useState } from 'react';
import type { RecognizedNote } from '../ink/recognize';
import { ledgerPositions, positionToY, type StaffLayout } from '../theory/staff';
import { ACCIDENTAL_GLYPHS, pitchName } from '../theory/pitch';
import { isConfident } from '../ink/symbolState';

export interface PlacedNote {
  key: string;
  note: RecognizedNote;
  /** Where the ink was. */
  fromX: number;
  fromY: number;
  /** Where the note belongs. */
  toX: number;
  toY: number;
}

/** Work out the target position of every recognised note. */
export function placeNotes(groups: RecognizedNote[][], layout: StaffLayout): PlacedNote[] {
  const placed: PlacedNote[] = [];
  for (const group of groups) {
    // Everything in a chord lines up on the group's average x.
    const targetX = group.reduce((sum, n) => sum + n.x, 0) / group.length;
    for (const note of group) {
      placed.push({
        key: note.strokeIds.join('~'),
        note,
        fromX: note.x,
        fromY: note.y,
        toX: targetX,
        toY: positionToY(layout, note.position),
      });
    }
  }
  return placed;
}

/**
 * Vertical offset of a ledger line relative to its notehead, in pixels.
 * Positive is downward, matching SVG's y axis: a ledger below a high note is a
 * positive offset, a ledger above a low note is negative.
 */
export function ledgerOffsetY(notePosition: number, ledgerPosition: number, lineGap: number): number {
  return ((notePosition - ledgerPosition) * lineGap) / 2;
}

/**
 * Does the learner want motion?
 *
 * Read at render rather than baked into a media query, because reduced motion
 * has to change *where the note starts*, not just how fast it travels. Shrinking
 * the duration to 1ms still animates — it just does it in one jump, which is the
 * one thing a vestibular preference is asking not to see.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function Note({ placed, lineGap }: { placed: PlacedNote; lineGap: number }) {
  const still = prefersReducedMotion();
  const [settled, setSettled] = useState(still);

  useEffect(() => {
    // Already where it belongs; there is nothing to trigger.
    if (still) return;
    // Two frames: the first commits the starting transform, the second triggers
    // the transition. One frame is not enough — React may batch both into one paint.
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setSettled(true));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, []);

  const { note, fromX, fromY, toX, toY } = placed;
  // Under reduced motion the note is simply drawn where it belongs and fades in:
  // a crossfade, with no travel from the ink's position at all.
  const x = settled || still ? toX : fromX;
  const y = settled || still ? toY : fromY;

  const rx = lineGap * 0.62;
  const ry = lineGap * 0.45;
  const ledgerHalf = rx * 1.75;
  // One threshold for "sure enough", shared with the pad's decision about
  // whether the learner's ink may fade. Two copies is how they came to disagree.
  const unsure = !isConfident(note.confidence);

  return (
    <g
      className={`resolved-note ${settled ? 'is-settled' : ''} ${unsure ? 'is-unsure' : ''}`}
      style={{ transform: `translate(${x}px, ${y}px)` }}
    >
      {ledgerPositions(note.position).map((p) => (
        <line
          key={p}
          className="resolved-ledger"
          x1={-ledgerHalf}
          x2={ledgerHalf}
          y1={ledgerOffsetY(note.position, p, lineGap)}
          y2={ledgerOffsetY(note.position, p, lineGap)}
        />
      ))}

      <ellipse className="resolved-head" rx={rx} ry={ry} transform="rotate(-22)" />

      {note.writtenAccidental !== null ? (
        <text className="resolved-accidental" x={-rx * 2.5} y={ry * 0.8} fontSize={lineGap * 1.5}>
          {ACCIDENTAL_GLYPHS[note.writtenAccidental] || '♮'}
        </text>
      ) : null}

      <text className="resolved-label" x={0} y={-lineGap * 1.25} fontSize={lineGap * 0.52}>
        {pitchName(note.pitch)}
        {note.pitch.octave}
      </text>
    </g>
  );
}

export interface ResolvedNotesProps {
  groups: RecognizedNote[][];
  layout: StaffLayout | null;
  width: number;
  height: number;
}

export function ResolvedNotes({ groups, layout, width, height }: ResolvedNotesProps) {
  if (!layout || !groups.length) return null;
  const placed = placeNotes(groups, layout);

  return (
    <svg className="resolved-layer" width={width} height={height} aria-hidden="true">
      {placed.map((p) => (
        <Note key={p.key} placed={p} lineGap={layout.lineGap} />
      ))}
    </svg>
  );
}
